// Stage 1 intake, built to be filled in under a minute while the customer is
// on the phone. Fees and final counts are deliberately absent: they belong to
// stage 2, on the onsite's own page, after the job.
//
// The same form edits stage 1 later, as long as the onsite is not canceled.

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  createOnsite,
  getOnsite,
  updateOnsite,
  listCustomers,
  listFranchisees,
  listTestTypes,
} from '../api.js'
import { useApiForm } from '../useApiForm.js'
import {
  Button,
  ButtonLink,
  DateField,
  Input,
  Notice,
  Panel,
  Select,
} from '../components/ui/index.js'
import { fromInstant } from '../vocab.js'
import { fieldFactory } from '../formField.jsx'

const EMPTY = {
  customerId: '',
  franchiseeId: '',
  requestingContactName: '',
  requestingContactPhone: '',
  requestedDate: '',
  requestedTime: '',
  siteName: '',
  siteAddress: '',
  estimatedDonorCount: '',
  instructions: '',
  clickupUrl: '',
}

// The request time is a date plus a clock time, combined into one instant.
// Splitting the controls beats a raw datetime string nobody formats right on
// the phone.
function toInstant(date, time) {
  if (!date) return null
  return new Date(`${date}T${time || '09:00'}`).toISOString()
}

export default function OnsiteForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const [form, setForm] = useState(isEdit ? null : EMPTY)
  const [estimates, setEstimates] = useState({})
  const [customers, setCustomers] = useState([])
  const [franchisees, setFranchisees] = useState([])
  const [testTypes, setTestTypes] = useState([])
  const [loadError, setLoadError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  useEffect(() => {
    Promise.all([listCustomers({ status: 'ACTIVE' }), listFranchisees(), listTestTypes()])
      .then(([customerData, franchiseeData, testData]) => {
        setCustomers(customerData.customers)
        setFranchisees(franchiseeData.franchisees)
        setTestTypes(testData.testTypes.filter((testType) => testType.isActive))
      })
      .catch((caught) => setLoadError(caught.message))
  }, [])

  useEffect(() => {
    if (!isEdit) return
    getOnsite(id)
      .then(({ onsite }) => {
        const requested = fromInstant(onsite.requestedAt)
        setForm({
          customerId: onsite.customerId,
          franchiseeId: onsite.franchiseeId ?? '',
          requestingContactName: onsite.requestingContactName ?? '',
          requestingContactPhone: onsite.requestingContactPhone ?? '',
          requestedDate: requested.date,
          requestedTime: requested.time,
          siteName: onsite.siteName ?? '',
          siteAddress: onsite.siteAddress ?? '',
          estimatedDonorCount: onsite.estimatedDonorCount ?? '',
          instructions: onsite.instructions ?? '',
          clickupUrl: onsite.clickupUrl ?? '',
        })
        setEstimates(
          Object.fromEntries(
            onsite.requestedTests.map((line) => [line.testTypeId, line.estimatedQuantity ?? '']),
          ),
        )
      })
      .catch((caught) => setLoadError(caught.message))
  }, [id, isEdit])

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function onSubmit(event) {
    event.preventDefault()
    const requestedTests = Object.entries(estimates)
      .filter(([, quantity]) => quantity !== '' && Number(quantity) >= 0)
      .map(([testTypeId, quantity]) => ({
        testTypeId,
        estimatedQuantity: quantity === '' ? null : Number(quantity),
      }))

    const payload = {
      customerId: form.customerId,
      franchiseeId: form.franchiseeId || null,
      requestingContactName: form.requestingContactName || null,
      requestingContactPhone: form.requestingContactPhone || null,
      requestedAt: toInstant(form.requestedDate, form.requestedTime),
      siteName: form.siteName || null,
      siteAddress: form.siteAddress || null,
      estimatedDonorCount:
        form.estimatedDonorCount === '' ? null : Number(form.estimatedDonorCount),
      instructions: form.instructions || null,
      clickupUrl: form.clickupUrl || null,
      requestedTests,
    }

    const result = await submit(() => (isEdit ? updateOnsite(id, payload) : createOnsite(payload)))
    if (result) navigate(`/onsites/${result.onsite.id}`)
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!form) return <div className="center-note">Loading</div>

  const field = fieldFactory({ form, change, fieldErrors })

  return (
    <div className="form-page">
      <header className="page-head">
        <h1>{isEdit ? 'Edit the request' : 'New onsite'}</h1>
      </header>

      <Panel>
        <form onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}

          <div className="formgrid">
            <Select
              id="customerId"
              className="span-2"
              label="Customer"
              value={form.customerId}
              onChange={(customerId) => change('customerId', customerId)}
              error={fieldErrors.customerId}
              disabled={isEdit}
              placeholder="Choose a customer"
              options={[
                { value: '', label: 'Choose a customer' },
                ...customers.map((customer) => ({ value: customer.id, label: customer.name })),
              ]}
            />

            {field('requestingContactName', 'Who called')}
            {field('requestingContactPhone', 'Callback number')}

            <DateField
              id="requested-date"
              label="Requested date"
              value={form.requestedDate}
              onChange={(value) => change('requestedDate', value)}
            />
            {field('requestedTime', 'Time', {
              type: 'time',
              help: 'Defaults to 9:00 if left blank.',
            })}

            {field('siteName', 'Site or facility', { wide: true })}
            {field('siteAddress', 'Site address', {
              wide: true,
              help: 'Mileage is measured from the collector home base to here.',
            })}

            <Select
              id="franchiseeId"
              label="Dispatched to"
              value={form.franchiseeId}
              onChange={(franchiseeId) => change('franchiseeId', franchiseeId)}
              help="Their home base address is copied onto the job for mileage."
              options={[
                { value: '', label: 'Not assigned yet' },
                ...franchisees.map((franchisee) => ({
                  value: franchisee.id,
                  label: franchisee.name,
                })),
              ]}
            />
            {field('estimatedDonorCount', 'Estimated donors', { type: 'number', min: '0' })}

            {field('clickupUrl', 'ClickUp task', {
              wide: true,
              placeholder: 'https://app.clickup.com/...',
            })}

            <Input
              id="instructions"
              className="span-2"
              label="Special instructions"
              multiline
              value={form.instructions}
              onChange={(event) => change('instructions', event.target.value)}
            />
          </div>

          <div className="form-section">
            <h2>Requested tests</h2>
            <p className="support">
              Estimates from the call. What was actually performed is recorded in the completed
              on-site summary and can differ.
            </p>
          </div>

          <div className="rows">
            {testTypes.map((testType) => (
              <div key={testType.id} className="row">
                <div className="row-main">
                  <div className="row-primary">{testType.name}</div>
                </div>
                <div className="row-trail">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    className="qty"
                    value={estimates[testType.id] ?? ''}
                    aria-label={`Estimated ${testType.name}`}
                    onChange={(event) =>
                      setEstimates((current) => ({ ...current, [testType.id]: event.target.value }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="btnrow">
            <ButtonLink to={isEdit ? `/onsites/${id}` : '/onsites'}>Cancel</ButtonLink>
            <span className="spacer" />
            <Button type="submit" variant="primary" block disabled={saving || !form.customerId}>
              {saving ? 'Saving' : isEdit ? 'Save request' : 'Create onsite'}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  )
}
